import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEnrollment } from '@/hooks/useEnrollments'
import { useCourse } from '@/hooks/useCourses'
import { useModulesByCourse } from '@/hooks/useModules'
import { useLessonsByModule, useLessonContentUrl } from '@/hooks/useLessons'
import { useProgress, useStartLesson, useCompleteLesson } from '@/hooks/useProgress'
import { useQuizByLesson, useQuestions } from '@/hooks/useQuizzes'
import { useAssignmentByLesson, useStudentSubmissions, useCreateSubmission } from '@/hooks/useAssignments'
import { CheckoutModal, BookingModal } from '@/components/modals'
import { VideoPlayer, TextContent, PDFViewer } from '@/components/lessons'
import { QuizTakingInterface, QuizResult } from '@/components/quiz'
import { FileUpload } from '@/components/common/FileUpload'
import { qrCodeService } from '@/services/qrCodeService'
import { scheduleService } from '@/services/scheduleService'
import { getScheduleTypeName, isUpcomingSchedule } from '@/types/schedule'
import type { QuizAttempt } from '@/types/quiz'
import type { Schedule } from '@/types/schedule'

export const ClassroomPage: React.FC = () => {
    const navigate = useNavigate()
    const { enrollmentId } = useParams<{ enrollmentId: string }>()

    // Fetch enrollment and course data
    const { data: enrollmentResponse, isLoading: enrollmentLoading } = useEnrollment(enrollmentId || '')
    const enrollment = enrollmentResponse?.data
    const { data: course } = useCourse(enrollment?.courseId || '')
    const { data: modules } = useModulesByCourse(enrollment?.courseId || '')
    const { data: progressData } = useProgress(enrollmentId || '')

    // Hybrid batch — QR code and schedules (only fetched when enrollment is hybrid_batch)
    const isHybridBatch = enrollment?.enrollmentType === 'hybrid_batch'
    const queryClient = useQueryClient()

    const {
        data: qrCodeResponse,
        isLoading: qrCodeLoading,
        error: qrCodeError,
    } = useQuery({
        queryKey: ['qrCode', enrollmentId],
        queryFn: () => qrCodeService.getForEnrollment(enrollmentId!),
        enabled: isHybridBatch && !!enrollmentId,
        retry: false,
    })

    const {
        data: schedulesResponse,
        isLoading: schedulesLoading,
    } = useQuery({
        queryKey: ['schedules', 'batch', enrollment?.batchId],
        queryFn: () => scheduleService.getSchedulesByBatch(enrollment!.batchId!),
        enabled: isHybridBatch && !!enrollment?.batchId,
    })

    const [qrRegenerating, setQrRegenerating] = useState(false)

    const handleRegenerateQR = async () => {
        if (!enrollmentId) return
        setQrRegenerating(true)
        try {
            await qrCodeService.regenerate(enrollmentId)
            queryClient.invalidateQueries({ queryKey: ['qrCode', enrollmentId] })
        } catch (err) {
            console.error('Failed to regenerate QR code:', err)
        } finally {
            setQrRegenerating(false)
        }
    }

    // Mutations
    const startLessonMutation = useStartLesson()
    const completeLessonMutation = useCompleteLesson()

    const [activeTab, setActiveTab] = useState('Syllabus')

    // Active module state
    const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
    const [activeLessonId, setActiveLessonId] = useState<string | null>(null)

    // Fetch lessons for active module
    const { data: lessons } = useLessonsByModule(activeModuleId || '')

    // UI State
    const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())
    const [showPaywall, setShowPaywall] = useState(false)
    const [showBooking, setShowBooking] = useState(false)

    // Quiz State
    const [showQuiz, setShowQuiz] = useState(false)
    const [quizResult, setQuizResult] = useState<QuizAttempt | null>(null)

    // Fetch quiz for active lesson
    const { data: quiz } = useQuizByLesson(activeLessonId || '')
    const { data: questions } = useQuestions(quiz?.id || '')

    // Assignment State
    const [showAssignment, setShowAssignment] = useState(false)
    const [submissionText, setSubmissionText] = useState('')
    const [fileUrl, setFileUrl] = useState('')
    const [fileName, setFileName] = useState('')

    // Fetch assignment for active lesson
    const { data: assignment } = useAssignmentByLesson(activeLessonId || '')
    const { data: studentSubmissions } = useStudentSubmissions(assignment?.id || '', enrollmentId || '')
    const createSubmissionMutation = useCreateSubmission()

    // Get latest submission (if exists)
    const latestSubmission = studentSubmissions && studentSubmissions.length > 0
        ? studentSubmissions[studentSubmissions.length - 1]
        : null

    // Initialize expanded modules and active module
    useEffect(() => {
        if (modules && modules.length > 0 && !activeModuleId) {
            const firstModule = modules[0]
            setActiveModuleId(firstModule.id)
            setExpandedModules(new Set([firstModule.id]))
        }
    }, [modules, activeModuleId])

    // Set first lesson as active when lessons are loaded
    useEffect(() => {
        if (lessons && lessons.length > 0 && !activeLessonId) {
            setActiveLessonId(lessons[0].id)
        }
    }, [lessons, activeLessonId])

    // Find active module and lesson
    const activeModule = modules?.find(m => m.id === activeModuleId)
    const activeLesson = lessons?.find(l => l.id === activeLessonId)

    // Fetch content URL for active lesson (only for non-text content)
    const { data: contentData } = useLessonContentUrl(
        activeLessonId || '',
        !!activeLesson && activeLesson.contentType !== 'text'
    )

    // Calculate completed lessons from progress data
    // apiClient auto-camelCases responses: lesson_id → lessonId
    const progressArray = (progressData as any) as Array<{ lessonId: string; status: string }> | undefined
    const completedLessonIds = new Set(
        progressArray?.filter((p) => p.status === 'completed').map((p) => p.lessonId) || []
    )
    const totalCompleted = progressArray?.filter((p) => p.status === 'completed').length || 0

    const toggleModule = (id: string) => {
        setExpandedModules(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const handleLessonSelect = (moduleId: string, lessonId: string) => {
        setActiveModuleId(moduleId)
        setActiveLessonId(lessonId)

        // Auto expand the module
        setExpandedModules(prev => new Set(prev).add(moduleId))

        // Start lesson if not already started
        if (enrollmentId) {
            startLessonMutation.mutate({
                enrollmentId,
                lessonId,
            })
        }
    }

    const handleCompleteLessonClick = async () => {
        if (!enrollmentId || !activeLessonId) return

        try {
            await completeLessonMutation.mutateAsync({
                enrollmentId,
                lessonId: activeLessonId,
                data: { lessonId: activeLessonId, completed: true }
            })

            // Success - button will update via refetch
        } catch (error) {
            console.error('Failed to complete lesson:', error)
        }
    }

    const handleNext = () => {
        if (!lessons || !modules) return

        const currentLessonIndex = lessons.findIndex(l => l.id === activeLessonId)

        // If there's a next lesson in current module
        if (currentLessonIndex < lessons.length - 1) {
            const nextLesson = lessons[currentLessonIndex + 1]
            handleLessonSelect(activeModuleId!, nextLesson.id)
        } else {
            // Move to next module
            const currentModuleIndex = modules.findIndex(m => m.id === activeModuleId)
            if (currentModuleIndex < modules.length - 1) {
                const nextModule = modules[currentModuleIndex + 1]
                setActiveModuleId(nextModule.id)
                // The useEffect will handle setting the first lesson
                setActiveLessonId(null)
            }
        }
    }

    // Quiz Handlers
    const handleStartQuiz = () => {
        setShowQuiz(true)
        setQuizResult(null)
    }

    const handleQuizComplete = async (result: QuizAttempt) => {
        setShowQuiz(false)
        setQuizResult(result)

        // Auto-mark lesson as complete when quiz is passed AND fully graded (no pending essay review)
        if (enrollmentId && activeLessonId && quiz && result.score >= quiz.passingScore && result.status !== 'needs_review') {
            try {
                await completeLessonMutation.mutateAsync({
                    enrollmentId,
                    lessonId: activeLessonId,
                    data: { lessonId: activeLessonId, completed: true }
                })
            } catch (e) {
                console.error('Failed to mark lesson complete after quiz', e)
            }
        }
    }

    const handleQuizCancel = () => {
        setShowQuiz(false)
    }

    const handleQuizRetry = () => {
        setQuizResult(null)
        setShowQuiz(true)
    }

    const handleCloseQuizResult = () => {
        setQuizResult(null)
    }

    // Assignment Handlers
    const handleStartAssignment = () => {
        setShowAssignment(true)
        // Do NOT pre-fill form - if submission exists, form will be disabled
        // User should see their previous submission in the "Submission Status" section instead
    }

    const handleCancelAssignment = () => {
        setShowAssignment(false)
        setSubmissionText('')
        setFileUrl('')
        setFileName('')
    }

    const handleSubmitAssignment = async () => {
        if (!assignment || !enrollmentId) return

        // At least one of text or file must be provided
        if (!submissionText.trim() && !fileUrl) {
            alert('Please provide either text answer or file upload')
            return
        }

        try {
            await createSubmissionMutation.mutateAsync({
                assignmentId: assignment.id,
                data: {
                    enrollmentId,
                    submissionText: submissionText.trim() || undefined,
                    fileUrl: fileUrl || undefined,
                    fileName: fileName || undefined,
                },
            })
            setShowAssignment(false)
            setSubmissionText('')
            setFileUrl('')
            setFileName('')
            alert('Assignment submitted successfully!')
        } catch (error) {
            console.error('Failed to submit assignment:', error)
            alert('Failed to submit assignment. Please try again.')
        }
    }

    const handleFileUpload = (url: string) => {
        setFileUrl(url)
        // Extract filename from URL
        const urlFileName = url.split('/').pop() || 'uploaded-file'
        setFileName(urlFileName)
    }

    // Render Content based on Type
    const renderContent = () => {
        // Show assignment submission form (FULL SCREEN OVERLAY)
        if (showAssignment && assignment) {
            return (
                <div className="absolute inset-0 bg-surface-dark z-50 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 px-6 py-4 bg-surface-card border-b border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="size-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-purple-400">assignment</span>
                            </div>
                            <div>
                                <h2 className="text-lg font-bold">{assignment.title}</h2>
                                <p className="text-xs text-gray-400">
                                    Max Score: {assignment.maxScore} | Passing: {assignment.passingScore}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleCancelAssignment}
                            className="size-10 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-auto p-6">
                        <div className="max-w-4xl mx-auto space-y-6">
                            {/* Instructions */}
                            {assignment.instructions && (
                                <div className="p-6 bg-surface-card rounded-2xl border border-white/5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="material-symbols-outlined text-primary">description</span>
                                        <h3 className="font-bold text-white">Instructions</h3>
                                    </div>
                                    <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{assignment.instructions}</p>
                                </div>
                            )}

                            {/* Submission Status */}
                            {latestSubmission && (
                                <div className={`p-6 rounded-2xl border ${
                                    latestSubmission.status === 'graded'
                                        ? 'bg-green-500/10 border-green-500/20'
                                        : 'bg-blue-500/10 border-blue-500/20'
                                }`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className={`material-symbols-outlined ${
                                            latestSubmission.status === 'graded' ? 'text-green-400' : 'text-blue-400'
                                        }`}>
                                            {latestSubmission.status === 'graded' ? 'check_circle' : 'pending'}
                                        </span>
                                        <h3 className={`font-bold ${
                                            latestSubmission.status === 'graded' ? 'text-green-400' : 'text-blue-400'
                                        }`}>
                                            {latestSubmission.status === 'graded' ? 'Graded' : 'Submitted - Pending Review'}
                                        </h3>
                                    </div>

                                    {/* Show submitted content */}
                                    {latestSubmission.submissionText && (
                                        <div className="mt-3 p-4 bg-surface-dark/50 rounded-xl">
                                            <p className="text-xs text-gray-400 mb-2">Your Answer:</p>
                                            <p className="text-white whitespace-pre-wrap">{latestSubmission.submissionText}</p>
                                        </div>
                                    )}
                                    {latestSubmission.fileName && (
                                        <div className="mt-3 p-4 bg-surface-dark/50 rounded-xl flex items-center gap-3">
                                            <span className="material-symbols-outlined text-blue-400">description</span>
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-400">Uploaded File:</p>
                                                <p className="text-white text-sm">{latestSubmission.fileName}</p>
                                            </div>
                                            {latestSubmission.fileUrl && (
                                                <a
                                                    href={latestSubmission.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30"
                                                >
                                                    Download
                                                </a>
                                            )}
                                        </div>
                                    )}

                                    {latestSubmission.status === 'graded' && (
                                        <>
                                            <div className="text-white text-2xl font-bold mt-4 pt-4 border-t border-white/10">
                                                Score: {latestSubmission.score}/{assignment.maxScore}
                                            </div>
                                            {latestSubmission.feedback && (
                                                <div className="mt-4 pt-4 border-t border-white/10">
                                                    <p className="text-sm text-gray-400 mb-2">Instructor Feedback:</p>
                                                    <p className="text-white leading-relaxed">{latestSubmission.feedback}</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Text Answer */}
                            <div>
                                <label className="block text-sm font-bold text-gray-400 uppercase mb-3">
                                    <span className="material-symbols-outlined text-sm align-middle mr-1">edit_note</span>
                                    Written Answer (Optional)
                                </label>
                                <textarea
                                    value={submissionText}
                                    onChange={(e) => setSubmissionText(e.target.value)}
                                    placeholder="Type your answer here..."
                                    className="w-full h-48 p-4 bg-surface-card border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!!latestSubmission}
                                />
                            </div>

                            {/* File Upload */}
                            <div>
                                <label className="block text-sm font-bold text-gray-400 uppercase mb-3">
                                    <span className="material-symbols-outlined text-sm align-middle mr-1">upload_file</span>
                                    File Upload (Optional)
                                </label>
                                {latestSubmission ? (
                                    <div className="p-4 bg-surface-card border border-white/10 rounded-2xl text-gray-400">
                                        File upload disabled - You have already submitted this assignment
                                    </div>
                                ) : (
                                    <FileUpload
                                        type="document"
                                        currentUrl={fileUrl}
                                        onUpload={handleFileUpload}
                                        label="Upload your assignment file (PDF, DOC, etc.)"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex-shrink-0 px-6 py-4 bg-surface-card border-t border-white/10 flex gap-3">
                        <button
                            onClick={handleCancelAssignment}
                            className="flex-1 px-6 py-3 rounded-xl bg-white/5 text-white font-medium hover:bg-white/10 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmitAssignment}
                            disabled={(!submissionText.trim() && !fileUrl) || createSubmissionMutation.isPending || !!latestSubmission}
                            className="flex-1 px-6 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {createSubmissionMutation.isPending ? 'Submitting...' :
                             latestSubmission?.status === 'graded' ? 'Already Graded' :
                             latestSubmission ? 'Already Submitted' : 'Submit Assignment'}
                        </button>
                    </div>
                </div>
            )
        }

        // Show quiz interface if active
        if (showQuiz && quiz && questions && enrollmentId) {
            return (
                <QuizTakingInterface
                    quiz={quiz}
                    questions={questions}
                    enrollmentId={enrollmentId}
                    onComplete={handleQuizComplete}
                    onCancel={handleQuizCancel}
                />
            )
        }

        if (!activeLesson) {
            return (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">school</span>
                    <h2 className="text-xl font-bold mb-2">Select a lesson to begin</h2>
                    <p className="text-gray-400">Choose from the syllabus on the left</p>
                </div>
            )
        }

        // For text content, use the contentUrl directly (it's the actual text)
        // For video/pdf, use the secure presigned URL
        const mediaUrl = activeLesson.contentType === 'text' ? activeLesson.contentUrl : contentData?.url

        if (activeLesson.contentType !== 'text' && !mediaUrl) {
            return (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-gray-400">Loading content...</p>
                </div>
            )
        }

        // ASSIGNMENT PREVIEW (show assignment info in main content)
        if (assignment && assignment.isPublished && activeLesson.contentType === 'text') {
            return (
                <div className="w-full h-full overflow-auto p-8 bg-surface-dark">
                    <div className="max-w-4xl mx-auto space-y-6">
                        {/* Assignment Header */}
                        <div className="flex items-start gap-4">
                            <div className="size-16 rounded-2xl bg-purple-500/20 flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-3xl text-purple-400">assignment</span>
                            </div>
                            <div className="flex-1">
                                <h1 className="text-3xl font-bold text-white mb-2">{assignment.title}</h1>
                                {assignment.description && (
                                    <p className="text-gray-400">{assignment.description}</p>
                                )}
                            </div>
                        </div>

                        {/* Assignment Details */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="p-4 bg-surface-card rounded-xl border border-white/5">
                                <div className="text-gray-400 text-sm mb-1">Max Score</div>
                                <div className="text-2xl font-bold text-white">{assignment.maxScore}</div>
                            </div>
                            <div className="p-4 bg-surface-card rounded-xl border border-white/5">
                                <div className="text-gray-400 text-sm mb-1">Passing Score</div>
                                <div className="text-2xl font-bold text-primary">{assignment.passingScore}</div>
                            </div>
                            <div className="p-4 bg-surface-card rounded-xl border border-white/5">
                                <div className="text-gray-400 text-sm mb-1">Status</div>
                                <div className="text-sm font-bold text-white">
                                    {latestSubmission?.status === 'graded' ? '✓ Graded' :
                                     latestSubmission?.status === 'submitted' ? '⏱ Pending' : 'Not Started'}
                                </div>
                            </div>
                        </div>

                        {/* Instructions */}
                        {assignment.instructions && (
                            <div className="p-6 bg-surface-card rounded-2xl border border-white/5">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="material-symbols-outlined text-primary">description</span>
                                    <h2 className="text-xl font-bold text-white">Instructions</h2>
                                </div>
                                <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                                    {assignment.instructions}
                                </div>
                            </div>
                        )}

                        {/* Your Submission */}
                        {latestSubmission && (
                            <div className={`p-6 rounded-2xl border ${
                                latestSubmission.status === 'graded'
                                    ? 'bg-green-500/10 border-green-500/20'
                                    : 'bg-blue-500/10 border-blue-500/20'
                            }`}>
                                <div className="flex items-center gap-2 mb-4">
                                    <span className={`material-symbols-outlined ${
                                        latestSubmission.status === 'graded' ? 'text-green-400' : 'text-blue-400'
                                    }`}>
                                        {latestSubmission.status === 'graded' ? 'check_circle' : 'pending'}
                                    </span>
                                    <h2 className={`text-xl font-bold ${
                                        latestSubmission.status === 'graded' ? 'text-green-400' : 'text-blue-400'
                                    }`}>
                                        Your Submission
                                    </h2>
                                </div>
                                {latestSubmission.status === 'graded' ? (
                                    <>
                                        <div className="text-white text-3xl font-bold mb-4">
                                            {latestSubmission.score}/{assignment.maxScore}
                                        </div>
                                        {latestSubmission.feedback && (
                                            <div className="mt-4 pt-4 border-t border-white/10">
                                                <p className="text-sm text-gray-400 mb-2">Instructor Feedback:</p>
                                                <p className="text-white leading-relaxed">{latestSubmission.feedback}</p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-gray-300">Your submission is being reviewed by the instructor.</p>
                                )}
                            </div>
                        )}

                        {/* File Types Info */}
                        {assignment.fileTypesAllowed && assignment.fileTypesAllowed.length > 0 && (
                            <div className="p-4 bg-surface-card/50 rounded-xl border border-white/5">
                                <div className="text-sm text-gray-400 mb-1">Accepted File Types:</div>
                                <div className="text-white font-mono text-sm">
                                    {assignment.fileTypesAllowed.join(', ')}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )
        }

        // Render based on content type - each component handles its own full-screen layout
        switch (activeLesson.contentType) {
            case 'video':
                return (
                    <div className="w-full h-full bg-black flex items-center justify-center p-0">
                        <div className="w-full max-h-full" style={{ aspectRatio: '16/9' }}>
                            <VideoPlayer
                                src={mediaUrl!}
                                onEnded={() => {
                                    if (!completedLessonIds.has(activeLessonId || '')) {
                                        handleCompleteLessonClick()
                                    }
                                }}
                            />
                        </div>
                    </div>
                )
            case 'pdf':
                return (
                    <div className="w-full h-full bg-black">
                        <PDFViewer src={mediaUrl!} />
                    </div>
                )
            case 'text':
                const textContent = activeLesson.contentHtml || activeLesson.contentUrl
                return (
                    <div className="w-full h-full overflow-y-auto px-8 md:px-16 py-8 custom-scrollbar bg-surface-dark">
                        <TextContent content={textContent} />
                    </div>
                )
            default:
                return (
                    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                        <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">error</span>
                        <p className="text-gray-400">Unsupported content type</p>
                    </div>
                )
        }
    }

    // Loading gate — tunggu data enrollment selesai di-fetch
    if (enrollmentLoading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    // Payment gate — blokir akses jika enrollment masih pending_payment
    if (enrollment?.status === 'pending_payment') {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
                <div className="w-full max-w-md text-center">
                    {/* Icon */}
                    <div className="flex items-center justify-center mx-auto mb-6 size-24 rounded-full bg-amber-100 dark:bg-amber-500/10">
                        <span className="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400">
                            hourglass_top
                        </span>
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-3">
                        Menunggu Konfirmasi Pembayaran
                    </h1>

                    {/* Description */}
                    <p className="text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
                        Pembayaran kamu sedang diverifikasi oleh admin. Akses materi akan dibuka setelah pembayaran dikonfirmasi.
                    </p>

                    {/* Batch Info (jika ada) */}
                    {enrollment.batchId && (
                        <div className="mb-6 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-left space-y-2">
                            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-sm">
                                <span className="material-symbols-outlined text-[18px]">calendar_month</span>
                                <span className="font-medium">Info Batch</span>
                            </div>
                            <div className="pl-6 text-sm text-slate-700 dark:text-slate-300">
                                <p className="font-semibold">{(enrollment as any).batchName || `Batch #${enrollment.batchId}`}</p>
                                {(enrollment as any).originalPrice !== undefined && (
                                    <p className="text-slate-500 dark:text-slate-400 mt-0.5">
                                        Harga:{' '}
                                        <span className="font-medium text-slate-700 dark:text-slate-200">
                                            {new Intl.NumberFormat('id-ID', {
                                                style: 'currency',
                                                currency: 'IDR',
                                                minimumFractionDigits: 0,
                                            }).format((enrollment as any).originalPrice)}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CTA Button */}
                    <button
                        onClick={() => navigate('/discover')}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-black font-bold hover:bg-primary/90 transition-all"
                    >
                        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                        Kembali ke Beranda
                    </button>
                </div>
            </div>
        )
    }

    const hasNextLesson = () => {
        if (!lessons || !modules || !activeLessonId) return false
        const currentLessonIndex = lessons.findIndex(l => l.id === activeLessonId)
        if (currentLessonIndex < lessons.length - 1) return true
        const currentModuleIndex = modules.findIndex(m => m.id === activeModuleId)
        return currentModuleIndex < modules.length - 1
    }

    const canProceedToNext = hasNextLesson() && completedLessonIds.has(activeLessonId || '')

    return (
        <div className="flex flex-col h-screen bg-surface-dark text-white font-lexend overflow-hidden">
            <CheckoutModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} course={course || { id: '', title: '', price: 0 }} />
            <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />

            {/* Header */}
            <header className="shrink-0 flex items-center bg-surface-dark/95 backdrop-blur-md px-4 py-3 justify-between border-b border-white/5 z-20">
                <button
                    onClick={() => navigate(-1)}
                    className="size-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                >
                    <span className="material-symbols-outlined">arrow_back</span>
                </button>
                <div className="flex flex-col items-center">
                    <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">{activeModule?.title}</h2>
                    <h3 className="text-sm font-bold truncate max-w-[200px] md:max-w-md">{activeLesson?.title || 'Select a lesson'}</h3>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleNext}
                        disabled={!canProceedToNext}
                        className={`hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${
                            canProceedToNext 
                                ? 'bg-white/10 hover:bg-white/20' 
                                : 'bg-white/5 text-gray-600 cursor-not-allowed opacity-50'
                        }`}
                        title={!canProceedToNext ? 'Selesaikan materi ini terlebih dahulu untuk lanjut' : 'Next Lesson'}
                    >
                        <span>Next</span>
                        <span className="material-symbols-outlined text-sm">arrow_forward</span>
                    </button>
                    <button
                        onClick={() => navigate(`/learn/${enrollmentId}/grades`)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs font-bold transition-colors"
                        title="View my grades"
                    >
                        <span className="material-symbols-outlined text-[16px]">grading</span>
                        <span className="hidden md:inline">Grades</span>
                    </button>
                    <div className="flex items-center gap-1 bg-surface-card px-3 py-1.5 rounded-full border border-white/10">
                        <span className="material-symbols-outlined text-[16px] text-gray-400">workspace_premium</span>
                        <span className="text-xs font-bold uppercase text-gray-400">{totalCompleted} Completed</span>
                    </div>
                </div>
            </header>

            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                {/* Main Stage */}
                <main className="flex-1 relative flex flex-col overflow-hidden md:order-2">
                    <div className="flex-1 overflow-hidden">
                        {renderContent()}
                    </div>

                    {/* Action Buttons */}
                    {activeLesson && !showQuiz && !showAssignment && (
                        <div className="p-4 bg-surface-dark border-t border-white/5">
                            <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                                {/* Mark Complete Button — only for pure content lessons (no quiz/assignment) */}
                                {!quiz && !assignment && (
                                <button
                                    onClick={handleCompleteLessonClick}
                                    disabled={completeLessonMutation.isPending || (activeLessonId ? completedLessonIds.has(activeLessonId) : false)}
                                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold transition-all ${
                                        completedLessonIds.has(activeLessonId || '')
                                            ? 'bg-green-500/20 text-green-400 cursor-default'
                                            : 'bg-white/10 text-white hover:bg-white/20 disabled:opacity-50'
                                    }`}
                                >
                                    <span className="material-symbols-outlined">
                                        {completedLessonIds.has(activeLessonId || '') ? 'check_circle' : 'check'}
                                    </span>
                                    <span>
                                        {completedLessonIds.has(activeLessonId || '') ? 'Completed' : completeLessonMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                                    </span>
                                </button>
                                )}

                                {/* Take Quiz Button (if quiz exists) */}
                                {quiz && quiz.isPublished && (
                                    <button
                                        onClick={handleStartQuiz}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-black font-bold hover:bg-primary/90 transition-all"
                                    >
                                        <span className="material-symbols-outlined">quiz</span>
                                        <span>Take Quiz</span>
                                    </button>
                                )}

                                {/* Take Assignment Button (if assignment exists) */}
                                {assignment && assignment.isPublished && (
                                    <button
                                        onClick={handleStartAssignment}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-all"
                                    >
                                        <span className="material-symbols-outlined">assignment</span>
                                        <span>
                                            {latestSubmission?.status === 'graded' ? 'View Assignment' :
                                             latestSubmission?.status === 'submitted' ? 'View Submission' : 'Take Assignment'}
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </main>

                {/* Sidebar */}
                <aside className="shrink-0 md:w-[400px] bg-surface-dark border-l border-white/5 flex flex-col md:order-1 h-[45vh] md:h-full z-10 shadow-xl">
                    <div className="flex border-b border-white/10">
                        {[
                            'Syllabus',
                            'Resources',
                            ...(enrollment?.enrollmentType === 'hybrid_batch' ? ['Jadwal & QR'] : []),
                        ].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 py-4 text-xs font-bold border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scrollbar p-4 pb-20">
                        {activeTab === 'Syllabus' && modules && (
                            <div className="flex flex-col gap-4">
                                {modules.map((module: any) => {
                                    const isExpanded = expandedModules.has(module.id)
                                    const isActiveModule = module.id === activeModuleId

                                    return (
                                        <div key={module.id} className="bg-surface-card rounded-xl overflow-hidden border border-white/5">
                                            <button
                                                onClick={() => {
                                                    toggleModule(module.id)
                                                    setActiveModuleId(module.id)
                                                }}
                                                className={`w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors ${isActiveModule ? 'bg-white/5' : ''}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className={`material-symbols-outlined text-[20px] ${isActiveModule ? 'text-primary' : 'text-gray-500'}`}>
                                                        book
                                                    </span>
                                                    <h4 className={`text-sm font-bold uppercase tracking-wide ${isActiveModule ? 'text-white' : 'text-gray-400'}`}>
                                                        {module.title}
                                                    </h4>
                                                </div>
                                                <span className={`material-symbols-outlined text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                    expand_more
                                                </span>
                                            </button>

                                            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                                {isActiveModule && lessons && (
                                                    <div className="p-3 pt-0 flex flex-col gap-1">
                                                        {lessons.map((lesson: any, index: number) => {
                                                            const isActive = lesson.id === activeLessonId
                                                            const isDone = completedLessonIds.has(lesson.id)
                                                            
                                                            // Lock logic: first lesson is free, subsequent ones require previous to be completed
                                                            const previousLesson = index > 0 ? lessons[index - 1] : null
                                                            const isLocked = previousLesson ? !completedLessonIds.has(previousLesson.id) : false

                                                            // Get icon based on content type
                                                            const getContentIcon = () => {
                                                                if (isLocked) return 'lock'
                                                                if (isDone) return 'check_circle'
                                                                switch (lesson.contentType) {
                                                                    case 'video':
                                                                        return 'play_circle'
                                                                    case 'pdf':
                                                                        return 'description'
                                                                    case 'text':
                                                                        return 'article'
                                                                    default:
                                                                        return 'school'
                                                                }
                                                            }

                                                            return (
                                                                <button
                                                                    key={lesson.id}
                                                                    onClick={() => {
                                                                        if (!isLocked) handleLessonSelect(module.id, lesson.id)
                                                                    }}
                                                                    disabled={isLocked}
                                                                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-all ${isActive
                                                                            ? 'bg-primary text-black font-bold shadow-lg shadow-primary/20'
                                                                            : isLocked
                                                                                ? 'text-gray-600 cursor-not-allowed opacity-50'
                                                                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                                                        }`}
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">
                                                                        {getContentIcon()}
                                                                    </span>
                                                                    <span className="text-xs truncate flex-1">{lesson.title}</span>
                                                                    <span className="text-[10px] opacity-70">{lesson.durationMinutes || 0}m</span>
                                                                </button>
                                                            )
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}

                        {activeTab === 'Resources' && (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                                <span className="material-symbols-outlined text-4xl text-gray-500 mb-4">folder</span>
                                <p className="text-gray-400">No resources available</p>
                            </div>
                        )}

                        {activeTab === 'Jadwal & QR' && isHybridBatch && (
                            <div className="flex flex-col gap-6">
                                {/* Section 1: QR Code */}
                                <div className="bg-surface-card rounded-2xl border border-white/5 p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="material-symbols-outlined text-primary">qr_code_2</span>
                                        <h3 className="font-bold text-white text-sm uppercase tracking-wide">QR Code Kehadiran</h3>
                                    </div>

                                    {qrCodeLoading ? (
                                        <div className="flex flex-col items-center py-8 gap-3">
                                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                                            <p className="text-gray-400 text-xs">Memuat QR code...</p>
                                        </div>
                                    ) : qrCodeError || !qrCodeResponse?.data?.imageUrl ? (
                                        <div className="flex flex-col items-center py-8 gap-3 text-center">
                                            <span className="material-symbols-outlined text-4xl text-gray-500">qr_code_off</span>
                                            <p className="text-gray-400 text-sm">
                                                QR code belum tersedia. Hubungi admin.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-4">
                                            {/* QR Image */}
                                            <div className="bg-white p-3 rounded-2xl shadow-lg">
                                                <img
                                                    src={qrCodeResponse.data.imageUrl}
                                                    alt="QR Code Kehadiran"
                                                    className="w-48 h-48 object-contain"
                                                />
                                            </div>

                                            {/* Expiry info */}
                                            <div className="text-center space-y-1">
                                                <p className="text-xs text-gray-500">Berlaku hingga</p>
                                                <p className="text-sm font-bold text-white">
                                                    {new Date(qrCodeResponse.data.expiresAt).toLocaleString('id-ID', {
                                                        dateStyle: 'medium',
                                                        timeStyle: 'short',
                                                    })}
                                                </p>
                                            </div>

                                            {/* Regenerate button */}
                                            <button
                                                onClick={handleRegenerateQR}
                                                disabled={qrRegenerating}
                                                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <span className={`material-symbols-outlined text-[16px] ${qrRegenerating ? 'animate-spin' : ''}`}>
                                                    {qrRegenerating ? 'autorenew' : 'refresh'}
                                                </span>
                                                {qrRegenerating ? 'Membuat ulang...' : 'Regenerate QR'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Section 2: Jadwal Sesi */}
                                <div className="bg-surface-card rounded-2xl border border-white/5 p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="material-symbols-outlined text-primary">calendar_month</span>
                                        <h3 className="font-bold text-white text-sm uppercase tracking-wide">Jadwal Sesi</h3>
                                    </div>

                                    {schedulesLoading ? (
                                        <div className="flex flex-col items-center py-8 gap-3">
                                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                                            <p className="text-gray-400 text-xs">Memuat jadwal...</p>
                                        </div>
                                    ) : !schedulesResponse?.data || schedulesResponse.data.length === 0 ? (
                                        <div className="flex flex-col items-center py-8 gap-3 text-center">
                                            <span className="material-symbols-outlined text-4xl text-gray-500">event_busy</span>
                                            <p className="text-gray-400 text-sm">Belum ada jadwal sesi.</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-3">
                                            {[...(schedulesResponse.data as Schedule[])].sort(
                                                (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
                                            ).map((session) => {
                                                const upcoming = isUpcomingSchedule(session)
                                                const sessionDate = new Date(session.dateTime)
                                                const sessionEnd = new Date(
                                                    sessionDate.getTime() + session.durationMinutes * 60000
                                                )

                                                return (
                                                    <div
                                                        key={session.id}
                                                        className={`rounded-xl border p-4 transition-all ${
                                                            upcoming
                                                                ? 'border-primary/30 bg-primary/5'
                                                                : 'border-white/5 bg-surface-dark/50 opacity-70'
                                                        }`}
                                                    >
                                                        {/* Session header */}
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                                    upcoming
                                                                        ? 'bg-primary/20 text-primary'
                                                                        : 'bg-white/10 text-gray-400'
                                                                }`}>
                                                                    Sesi {session.sessionNumber}
                                                                </span>
                                                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                    session.type === 'theory' ? 'bg-blue-500/20 text-blue-400' :
                                                                    session.type === 'practical' ? 'bg-orange-500/20 text-orange-400' :
                                                                    session.type === 'assessment' ? 'bg-red-500/20 text-red-400' :
                                                                    'bg-purple-500/20 text-purple-400'
                                                                }`}>
                                                                    {getScheduleTypeName(session.type)}
                                                                </span>
                                                            </div>
                                                            {upcoming ? (
                                                                <span className="flex items-center gap-1 text-xs text-green-400">
                                                                    <span className="material-symbols-outlined text-[14px]">schedule</span>
                                                                    Mendatang
                                                                </span>
                                                            ) : (
                                                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                                    Selesai
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Title */}
                                                        <p className="text-sm font-bold text-white mb-2">{session.title}</p>

                                                        {/* Date & time */}
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
                                                            <span className="material-symbols-outlined text-[14px]">event</span>
                                                            <span>
                                                                {sessionDate.toLocaleDateString('id-ID', {
                                                                    weekday: 'short',
                                                                    day: 'numeric',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                })}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
                                                            <span className="material-symbols-outlined text-[14px]">schedule</span>
                                                            <span>
                                                                {sessionDate.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                                                {' – '}
                                                                {sessionEnd.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                                                {' '}({session.durationMinutes} menit)
                                                            </span>
                                                        </div>

                                                        {/* Location */}
                                                        {session.location && (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                                                <span className="material-symbols-outlined text-[14px]">location_on</span>
                                                                <span className="truncate">{session.location}</span>
                                                            </div>
                                                        )}

                                                        {/* Meeting link */}
                                                        {session.meetingLink && (
                                                            <div className="flex items-center gap-1.5 text-xs mt-1">
                                                                <span className="material-symbols-outlined text-[14px] text-blue-400">videocam</span>
                                                                <a
                                                                    href={session.meetingLink}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-400 hover:text-blue-300 underline truncate"
                                                                >
                                                                    Bergabung ke sesi online
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            {/* Quiz Result Modal */}
            {quizResult && quiz && (
                <QuizResult
                    attempt={quizResult}
                    maxAttempts={quiz.maxAttempts}
                    passingScore={quiz.passingScore}
                    hasEssay={questions?.some(q => q.questionType === 'essay') || false}
                    hasMC={questions?.some(q => q.questionType === 'multiple_choice') || false}
                    onRetry={handleQuizRetry}
                    onClose={handleCloseQuizResult}
                />
            )}
        </div>
    )
}

export default ClassroomPage
